import { useState, ReactNode } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, ArrowRight } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  localISODate,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type SalesOrder = {
  id: number
  number: string
  partner: number
  partner_name?: string
  order_date: string
  expected_delivery_date: string | null
  status: 'DRAFT' | 'CONFIRMED' | 'DELIVERED' | 'INVOICED' | 'CANCELLED'
  currency_code: string
  total: string
  invoice: number | null
  invoice_number: string | null
}

type Partner = { id: number; code: string; name: string; partner_type: string; is_active: boolean }

// Minimal create payload — rest filled in on the record page.
type CreateInput = {
  number: string
  partner: number | 0
  order_date: string
  expected_delivery_date: string | null
  currency_code: string
}

const STATUS_COLOR: Record<SalesOrder['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  DELIVERED: 'bg-purple-100 text-purple-800',
  INVOICED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-700',
}

const STATUS_FILTERS: ('ALL' | SalesOrder['status'])[] = [
  'ALL', 'DRAFT', 'CONFIRMED', 'DELIVERED', 'INVOICED', 'CANCELLED',
]

export default function SalesOrders() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const ordersQ = useQuery({
    queryKey: ['sales', 'orders'],
    queryFn: async () => (await api.get<SalesOrder[]>('/sales/orders/')).data,
  })
  const partnersQ = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<Partner[]>('/partners/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [filterStatus, setFilterStatus] = useState<'ALL' | SalesOrder['status']>('ALL')

  function openCreate() {
    setDraft({
      ...emptyDraft(),
      number: `SO-${Date.now().toString().slice(-6)}`,
    })
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<SalesOrder>('/sales/orders/', {
      ...draft,
      partner: Number(draft.partner),
      status: 'DRAFT',
      delivery_address: '',
      notes: '',
      lines: [],
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['sales', 'orders'] })
      toast.success('Order created')
      setCreating(false)
      navigate(`/admin/sales/orders/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/sales/orders/${id}/`),
    onSuccess: () => {
      toast.success('Order deleted')
      qc.invalidateQueries({ queryKey: ['sales', 'orders'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const convertMut = useMutation({
    mutationFn: async (id: number) => (await api.post(`/sales/orders/${id}/convert_to_invoice/`)).data,
    onSuccess: (data: any) => {
      toast.success('Converted to invoice')
      qc.invalidateQueries({ queryKey: ['sales', 'orders'] })
      qc.invalidateQueries({ queryKey: ['finance', 'invoices'] })
      if (data?.id) navigate(`/admin/finance/invoices/${data.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const orders = ordersQ.data || []
  const partners = (partnersQ.data || []).filter(
    (p) => p.is_active && (p.partner_type === 'CUSTOMER' || p.partner_type === 'BOTH')
  )
  const filtered = filterStatus === 'ALL'
    ? orders
    : orders.filter((o) => o.status === filterStatus)

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Sales Orders</h2>
          <p className="text-sm text-muted-foreground">
            {orders.length} total · {orders.filter((o) => o.status === 'CONFIRMED' || o.status === 'DELIVERED').length} in flight
          </p>
        </div>
        <Button onClick={openCreate} disabled={partners.length === 0}>
          <Plus className="h-3 w-3 mr-1" /> New order
        </Button>
      </div>

      {partners.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3 rounded-lg mb-4">
          Add at least one active <strong>customer</strong> under Partners before creating orders.
        </div>
      )}

      <div className="mb-4 flex items-center space-x-2 text-sm">
        <span className="text-muted-foreground">Status:</span>
        {STATUS_FILTERS.map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1 rounded ${filterStatus === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {ordersQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">No orders.</div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Number</Th>
                <Th>Date</Th>
                <Th>Customer</Th>
                <Th>Status</Th>
                <Th>Invoiced as</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((o) => (
                <tr
                  key={o.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/sales/orders/${o.id}`)}
                >
                  <Td className="font-mono text-xs">{o.number}</Td>
                  <Td className="text-xs">{o.order_date}</Td>
                  <Td>{o.partner_name}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[o.status]}`}>{o.status}</span>
                  </Td>
                  <Td className="text-xs">
                    {o.invoice_number ? (
                      <Link
                        to={`/admin/finance/invoices/${o.invoice}`}
                        onClick={(ev) => ev.stopPropagation()}
                        className="text-blue-600 hover:underline inline-flex items-center gap-1 font-mono"
                      >
                        <ArrowRight className="h-3 w-3" /> {o.invoice_number}
                      </Link>
                    ) : <span className="text-muted-foreground">—</span>}
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/sales/orders/${o.id}`)}>
                          Open
                        </DropdownMenuItem>
                        {o.status === 'CONFIRMED' && (
                          <DropdownMenuItem
                            onClick={() => navigate(`/admin/sales/orders/${o.id}`)}
                            className="text-purple-700"
                          >
                            Deliver…
                          </DropdownMenuItem>
                        )}
                        {o.status === 'DELIVERED' && !o.invoice && (
                          <DropdownMenuItem
                            onClick={() => confirm(
                              `Convert order ${o.number} to a customer invoice`,
                              () => convertMut.mutate(o.id),
                            )}
                            disabled={convertMut.isPending}
                            className="text-green-700"
                          >
                            Convert to invoice
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          disabled={!!o.invoice}
                          onClick={() => !o.invoice && confirm(
                            `order ${o.number}`,
                            () => deleteMut.mutate(o.id),
                          )}
                          className={!o.invoice ? 'text-destructive' : ''}
                        >
                          {o.invoice ? 'Delete (invoiced)' : 'Delete'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create drawer — minimal fields. Lines + delivery + notes on the record page. */}
      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New sales order"
        description="Create the order header. Add line items, delivery address, and notes on the record page. Confirm → Deliver → Convert to invoice."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="so-num">Number *</FieldLabel>
              <Input id="so-num" autoFocus required value={draft.number}
                onChange={(ev) => setDraft({ ...draft, number: ev.target.value })}
                className="font-mono" />
            </Field>
            <Field>
              <FieldLabel htmlFor="so-cur">Currency</FieldLabel>
              <Input id="so-cur" value={draft.currency_code}
                onChange={(ev) => setDraft({ ...draft, currency_code: ev.target.value.toUpperCase().slice(0, 3) })}
                className="font-mono" maxLength={3} />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="so-customer">Customer *</FieldLabel>
            <SearchableSelect
              id="so-customer"
              value={draft.partner ? String(draft.partner) : undefined}
              onChange={(v) => setDraft({ ...draft, partner: Number(v) })}
              placeholder="— select —"
              options={partners.map((p) => ({
                value: String(p.id), label: p.name, hint: p.code,
              }))}
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="so-date">Order date *</FieldLabel>
              <DatePicker id="so-date" required value={draft.order_date}
                onChange={(v) => setDraft({ ...draft, order_date: v })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="so-delivery">Expected delivery</FieldLabel>
              <DatePicker id="so-delivery" value={draft.expected_delivery_date || ''}
                onChange={(v) => setDraft({ ...draft, expected_delivery_date: v || null })} />
            </Field>
          </div>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return {
    number: '',
    partner: 0,
    order_date: localISODate(new Date()),
    expected_delivery_date: null,
    currency_code: 'EUR',
  }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
